import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import z from "zod";

import { isAdminRole } from "@/auth/shared";
import { Alert } from "@/components/base/alert";
import { Button } from "@/components/base/button";
import {
  getConstantContactAuthUrlQueryOptions,
  getConstantContactStatusQueryOptions,
} from "@/functions/admin/constant-contact";
import { DefaultLayout } from "@/layouts/default";

const searchSchema = z.object({
  connected: z.boolean().optional(),
  error: z.string().optional(),
});

export const Route = createFileRoute("/admin/constant-contact")({
  validateSearch: zodValidator(searchSchema),
  loader: async ({ context }) => {
    const viewer = context.viewer;

    if (!viewer || !isAdminRole(viewer.role)) {
      throw redirect({ to: "/not-found" });
    }

    await Promise.all([
      // Always fetch fresh status — never use cached data that may be stale
      // after an OAuth redirect
      context.queryClient.fetchQuery(getConstantContactStatusQueryOptions()),
      context.queryClient.ensureQueryData(
        getConstantContactAuthUrlQueryOptions(),
      ),
    ]);
  },
  component: RouteComponent,
});

function RouteComponent() {
  const search = Route.useSearch();
  const { data: status } = useSuspenseQuery(
    getConstantContactStatusQueryOptions(),
  );
  const { data: authUrl } = useSuspenseQuery(
    getConstantContactAuthUrlQueryOptions(),
  );

  return (
    <DefaultLayout
      classNames={{
        content: "flex flex-col space-y-6 max-w-2xl px-3 py-12 mx-auto",
      }}
    >
      <h1 className="text-2xl font-bold">Constant Contact</h1>

      {search.connected && (
        <Alert
          color="success"
          title="Connected"
          description="Constant Contact has been connected successfully."
        />
      )}

      {search.error && (
        <Alert color="error" title="Error" description={search.error} />
      )}

      <div className="space-y-4 rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold">Connection Status</h2>

        {status?.connected ? (
          <div className="space-y-2">
            <p className="font-medium text-green-700">Connected</p>
            {status.expiresAt && (
              <p className="text-sm text-gray-600">
                Token expires: {new Date(status.expiresAt).toLocaleString()}
              </p>
            )}
          </div>
        ) : (
          <p className="text-gray-500">Not connected</p>
        )}

        {authUrl?.url ? (
          <Button
            color="primary"
            onPress={() => {
              window.location.href = authUrl.url!;
            }}
          >
            {status?.connected ? "Reconnect" : "Connect"} Constant Contact
          </Button>
        ) : (
          <p className="text-sm text-gray-500">
            Constant Contact client ID is not configured. Set the{" "}
            <code className="rounded bg-gray-100 px-1">
              CONSTANT_CONTACT_CLIENT_ID
            </code>{" "}
            environment variable.
          </p>
        )}
      </div>
    </DefaultLayout>
  );
}
